/**
 * 类型趋势图子组件
 * 内部调用 useTimelineData Hook 请求数据，使用 CardCommon 包装
 */
import React, { useState, useMemo, useCallback } from 'react';
import CardCommon from '@op/components/CardCommon';
import { lineIcon, barIcon, tableIcon } from '@op/constants/chart';
import useLanguage from '@op/hooks/useLanguage';
import { useTimelineData } from '../../hooks/useTimelineData';
import TypeTrendEChart from './TypeTrendEChart';
import TypeTrendTable from './TypeTrendTable';
import type { ITypeTrendChartProps, TChartType, IDimensionMap } from './types';

const commonHeight = 319;

const TypeTrendChart: React.FC<ITypeTrendChartProps> = ({
  title,
  metric,
  dimensionType,
  filter,
  gameInfo,
}) => {
  // ==================== 状态管理（每个图表独立） ====================
  const [chartType, setChartType] = useState<TChartType>('line');

  // ==================== 数据请求（内部调用 useTimelineData） ====================
  const {
    chartData,
    loading,
    isEmpty,
    download,
    dimensionMap: apiDimensionMap,
  } = useTimelineData({
    filter,
    gameInfo,
    metric,
    topN: 10,
    downloadConfig: {
      fileName: `Ad Creatives ${title || metric}`,
    },
  });

  // ==================== 多语言 ====================
  const { isEn, formatMessage } = useLanguage();

  // ==================== 互动类型维度映射（手动配置） ====================
  const ENGAGEMENT_MAP: IDimensionMap = useMemo(
    () => ({
      view: { en: 'View', zh: formatMessage('View') },
      like: { en: 'Like', zh: formatMessage('Like') },
      comment: { en: 'Comment', zh: formatMessage('Comment') },
      share: { en: 'Share', zh: formatMessage('Share') },
    }),
    [formatMessage],
  );

  const dimensionMap =
    dimensionType === 'engagement' ? ENGAGEMENT_MAP : apiDimensionMap;

  // ==================== 过滤 engagement_stat 数据 ====================
  const filteredChartData = useMemo(() => {
    if (!chartData || chartData.isEmpty) return chartData;

    // 过滤掉 legend/series 中 key 为 engagement_stat 的数据
    const filteredSeries = (chartData.series || []).filter(
      (item) => item.name !== 'engagement_stat',
    );
    const filteredLegend = (chartData.legend || []).filter(
      (name) => name !== 'engagement_stat',
    );

    return {
      ...chartData,
      series: filteredSeries,
      legend: filteredLegend,
    };
  }, [chartData]);

  const getDimensionName = useCallback(
    (key: string) => {
      const dimension = dimensionMap[key];
      if (!dimension) return key;
      return isEn ? dimension.en : dimension.zh;
    },
    [dimensionMap, isEn],
  );

  const handleTypeDownload = useCallback(() => {
    if (!download || filteredChartData?.isEmpty) return;
    const columnHeaders = Object.fromEntries(
      (filteredChartData.legend || []).map((name: string) => {
        const dimension = dimensionMap[name];
        return [name, dimension.en || name];
      }),
    );
    download({ columnHeaders });
  }, [filteredChartData, download, getDimensionName]);

  // ==================== 操作按钮配置 ====================
  const operations = [lineIcon, barIcon, tableIcon];

  // ==================== 渲染逻辑 ====================
  return (
    <CardCommon
      title={title}
      operations={operations}
      operationInfo={[chartType, setChartType]}
      loading={loading}
      empty={isEmpty}
      hasDownload={true}
      loadingHeight={commonHeight}
      emptyHeight={commonHeight}
      handleDownloadOnClick={handleTypeDownload}
    >
      <div className="dbo-type-trend-chart">
        {chartType === 'table' ? (
          <TypeTrendTable
            height={commonHeight}
            chartData={filteredChartData}
            dimensionMap={dimensionMap}
          />
        ) : (
          <TypeTrendEChart
            chartData={filteredChartData}
            chartType={chartType}
            dimensionMap={dimensionMap}
            height={commonHeight}
            filter={filter}
          />
        )}
      </div>
    </CardCommon>
  );
};

export default React.memo(TypeTrendChart);
